/**
 * Print orders (printers): each order's stage as a tap-through stepper, a
 * proof the customer approves before printing, and whether it's been paid.
 */
import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useI18n, useT } from '../lib/i18n';
import { compressImage } from '../lib/image';
import { formatDue, inr } from '../lib/format';
import {
  Badge,
  Card,
  EmptyState,
  ErrorNote,
  Meta,
  PageHero,
  Spinner,
} from '../components/Primitives';
import { Icon } from '../components/Icon';

interface Order {
  id: string;
  title: string;
  city: string;
  neededBy: string;
  status: string;
  stage: string;
  amountInr: number | null;
  deliveryAddress: string | null;
  artworkUrl: string | null;
  proof: { url: string; status: string | null; note: string | null } | null;
  dispatchNote: string | null;
  payment: {
    status: string;
    netInr: number;
    dueOn: string | null;
    paidAt: string | null;
  } | null;
}

const NEXT: Record<string, string[]> = {
  accepted: ['printing'],
  proof_approved: ['printing'],
  printing: ['ready'],
  ready: ['dispatched', 'delivered', 'installed'],
  dispatched: ['delivered', 'installed'],
};

export function OrdersPage() {
  const T = useT();
  const [data, setData] = useState<Order[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () =>
    api.get<{ orders: Order[] }>('/v1/partner/orders').then(
      (r) => setData(r.orders),
      (e: unknown) => setError(e instanceof ApiError ? e.message : 'Error'),
    );
  useEffect(() => {
    void load();
  }, []);
  if (error) return <ErrorNote message={error} onRetry={() => void load()} />;
  if (!data) return <Spinner />;
  const open = data.filter((o) => !['delivered', 'installed'].includes(o.stage));
  const owed = data
    .filter((o) => o.payment && o.payment.status !== 'paid')
    .reduce((sum, o) => sum + (o.payment?.netInr ?? 0), 0);
  const hero = (
    <PageHero
      icon="orders"
      title={T('ऑर्डर', 'Orders')}
      sub={T(
        'हर ऑर्डर को अगले कदम पर ले जाएँ — ग्राहक को हर बदलाव दिखता है।',
        'Move each order to its next step — the customer sees every change.',
      )}
      stats={[
        { label: T('चल रहे', 'Open'), value: String(open.length) },
        {
          label: T('भेजने को तैयार', 'Ready to send'),
          value: String(data.filter((o) => o.stage === 'ready').length),
        },
        { label: T('आना बाकी', 'To be paid'), value: inr(owed) },
      ]}
    />
  );
  if (data.length === 0)
    return (
      <>
        {hero}
        <EmptyState
          icon="orders"
          title={T('अभी कोई ऑर्डर नहीं', 'No orders yet')}
          body={T('जो काम आपको मिलेगा, यहाँ दिखेगा।', 'Work you win shows here.')}
        />
      </>
    );
  return (
    <div className="space-y-3">
      {hero}
      {data.map((o) => (
        <OrderCard key={o.id} o={o} onChange={() => void load()} />
      ))}
    </div>
  );
}

function OrderCard({ o, onChange }: { o: Order; onChange: () => void }) {
  const T = useT();
  const { lang } = useI18n();
  const STAGE: Record<string, string> = {
    accepted: T('मिला', 'Accepted'),
    proof_sent: T('प्रूफ़ भेजा', 'Proof sent'),
    proof_approved: T('प्रूफ़ पास', 'Proof approved'),
    printing: T('छप रहा है', 'Printing'),
    ready: T('तैयार', 'Ready'),
    dispatched: T('भेज दिया', 'Dispatched'),
    delivered: T('पहुँच गया', 'Delivered'),
    installed: T('लगा दिया', 'Installed'),
  };
  const PAY: Record<string, string> = {
    pending: T('काम जाँच के बाद', 'After the job is checked'),
    approved: T('मंज़ूर — आने वाला है', 'Approved — on its way'),
    paid: T('मिल गया', 'Paid'),
    held: T('रुका हुआ', 'On hold'),
    disputed: T('विवाद में', 'Disputed'),
  };
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [courier, setCourier] = useState('');
  const steps = ['accepted', 'printing', 'ready', 'dispatched', 'delivered'];
  const at = Math.max(
    0,
    steps.indexOf(
      o.stage === 'proof_sent' || o.stage === 'proof_approved'
        ? 'accepted'
        : o.stage === 'installed'
          ? 'delivered'
          : o.stage,
    ),
  );

  const go = async (stage: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await api.post(`/v1/partner/orders/${o.id}/stage`, {
        stage,
        ...(stage === 'dispatched' && courier ? { note: courier } : {}),
      });
      onChange();
    } catch (err) {
      setMsg(
        err instanceof ApiError ? err.message : T('कुछ गड़बड़ हुई।', 'Something went wrong.'),
      );
    } finally {
      setBusy(false);
    }
  };
  const sendProof = async (file: File) => {
    setBusy(true);
    setMsg(T('प्रूफ़ भेजा जा रहा है…', 'Sending the proof…'));
    try {
      const img = await compressImage(file);
      const up = await api.post<{ url: string }>('/v1/partner/uploads', { data: img.dataUrl });
      await api.post(`/v1/partner/orders/${o.id}/proof`, { url: up.url });
      setMsg(null);
      onChange();
    } catch (err) {
      setMsg(
        err instanceof ApiError
          ? err.message
          : T('नहीं गया। दोबारा कोशिश करें।', 'It didn’t go. Try again.'),
      );
    } finally {
      setBusy(false);
    }
  };

  const done = o.stage === 'delivered' || o.stage === 'installed';
  return (
    <Card>
      <div className="flex items-start gap-3">
        <span className="icon-tile h-11 w-11">
          <Icon
            name={done ? 'check' : o.stage === 'dispatched' ? 'truck' : 'printer'}
            className="h-6 w-6"
          />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-[17px] font-bold leading-snug">{o.title}</p>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <Badge tone={done ? 'good' : 'warn'} pulse={!done}>
              {STAGE[o.stage] ?? o.stage}
            </Badge>
            <Meta icon="listings">{o.city}</Meta>
            {!done && <Meta icon="clock">{formatDue(o.neededBy, lang)}</Meta>}
          </div>
        </div>
        {o.amountInr ? (
          <span className="tnum shrink-0 font-display text-lg font-bold">
            {inr(o.amountInr)}
          </span>
        ) : null}
      </div>

      <ol
        className="mt-4 grid grid-cols-5 gap-1"
        aria-label={T('ऑर्डर कहाँ तक पहुँचा', 'Order progress')}
      >
        {steps.map((s, i) => (
          <li key={s} className="flex flex-col items-center gap-1.5 text-center">
            <span
              className={`h-1.5 w-full rounded-full ${i <= at ? '' : 'bg-panel-2'}`}
              style={
                i <= at
                  ? {
                      backgroundImage: 'linear-gradient(90deg, var(--sec-from), var(--sec-to))',
                    }
                  : undefined
              }
            />
            <span
              className={`text-[10.5px] leading-tight ${i === at ? 'font-bold' : 'text-muted'}`}
              style={i === at ? { color: 'var(--sec-ink)' } : undefined}
            >
              {STAGE[s]}
            </span>
          </li>
        ))}
      </ol>

      {o.artworkUrl && (
        <a className="btn-soft mt-4" href={o.artworkUrl} target="_blank" rel="noreferrer">
          <Icon name="download" className="h-5 w-5" />
          {T('डिज़ाइन फ़ाइल डाउनलोड करें', 'Download the artwork file')}
        </a>
      )}
      {o.proof && (
        <div className="mt-3 flex items-start gap-2 rounded-2xl bg-panel-2 p-3 text-sm">
          <Icon name="file" className="mt-0.5 h-4 w-4 shrink-0 text-muted" />
          <div>
            {T('प्रूफ़', 'Proof')}:{' '}
            <b>
              {o.proof.status === 'waiting'
                ? T('ग्राहक देख रहा है', 'With the customer')
                : o.proof.status === 'approved'
                  ? T('पास ✓', 'Approved ✓')
                  : T('बदलाव चाहिए', 'Changes asked')}
            </b>
            {o.proof.note && <span className="block text-xs text-muted">“{o.proof.note}”</span>}
          </div>
        </div>
      )}
      <p className="mt-3 flex items-center gap-2 text-[13px] text-muted">
        <Icon name="payouts" className="h-4 w-4 shrink-0" />
        <span>
          {T('पेमेंट', 'Payment')}:{' '}
          {o.payment
            ? `${PAY[o.payment.status] ?? o.payment.status}${o.payment.netInr ? ` · ${inr(o.payment.netInr)}` : ''}`
            : T('काम पूरा होने के बाद', 'After the job is done')}
        </span>
      </p>
      {o.stage === 'ready' && (
        <input
          className="mt-3"
          value={courier}
          onChange={(e) => setCourier(e.target.value)}
          placeholder={T('कूरियर / ट्रैकिंग (ज़रूरी नहीं)', 'Courier / tracking (optional)')}
        />
      )}
      <div className="mt-3 flex flex-col gap-2">
        {(NEXT[o.stage] ?? []).map((s, i) => (
          <button
            key={s}
            className={i === 0 ? 'btn-primary' : 'btn-secondary'}
            disabled={busy}
            onClick={() => void go(s)}
          >
            {T('आगे', 'Mark as')}: {STAGE[s]}
            {i === 0 && <Icon name="arrow" className="h-5 w-5" strokeWidth={2.2} />}
          </button>
        ))}
        {['accepted', 'proof_sent', 'proof_approved'].includes(o.stage) &&
          o.proof?.status !== 'approved' && (
            <label className="btn-soft cursor-pointer">
              <Icon name="camera" className="h-5 w-5" />
              {o.proof
                ? T('नया प्रूफ़ भेजें', 'Send a new proof')
                : T('प्रूफ़ भेजें (छापने से पहले)', 'Send a proof (before printing)')}
              <input
                type="file"
                accept="image/*"
                className="sr-only"
                disabled={busy}
                onChange={(e) => e.target.files?.[0] && void sendProof(e.target.files[0])}
              />
            </label>
          )}
      </div>
      {o.dispatchNote && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-muted">
          <Icon name="truck" className="h-4 w-4" /> {o.dispatchNote}
        </p>
      )}
      {msg && <p className="mt-2 text-sm text-muted">{msg}</p>}
    </Card>
  );
}
